import {useIntl} from 'react-intl'
import useSiteType from '../../hooks/use-site-type'

/**
 * Custom hook to generate account privacy fields for a form.
 *
 * This hook uses the `useIntl` hook from `react-intl` to localize field labels.
 * It returns an object containing configuration for privacy-related form fields,
 * such as "analytical" and "promo" checkboxes.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.form - The form object containing control and form state.
 * @param {Object} params.form.control - The control object used for form management.
 * @param {Object} params.form.formState - The state of the form, including errors.
 * @param {Object} params.form.formState.errors - An object containing form validation errors.
 * @param {string} [params.prefix=''] - An optional prefix for field names.
 * @returns {Object} An object containing field configurations for the form.
 * @returns {Object} return.analytical - Configuration for the analytical field.
 * @returns {Object} return.promo - Configuration for the promo field.
 */
export default function useAccountPrivacyFields({
    form: {
        control,
        formState: {errors}
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const {isUS} = useSiteType()

    const fields = {
        analytical: {
            name: `${prefix}analytical`,
            label: formatMessage({
                defaultMessage: `I agree to my data and purchase preferences being shared only within the Vilebrequin family for analysis and for a unique and personalized experience`,
                id: 'account_privacy.form.checkbox.analytical'
            }),
            type: isUS ? 'hidden' : 'checkbox',
            defaultValue: false,
            autoComplete: 'honorific-prefix',
            error: errors?.[`${prefix}analytical`],
            control
        },
        promo: {
            name: `${prefix}promo`,
            label: formatMessage({
                defaultMessage: `I agree to receive the latest news and promotional offers via the means of communication that I have sent to you.`,
                id: 'account_privacy.form.checkbox.promo'
            }),
            type: 'checkbox',
            defaultValue: false,
            autoComplete: 'honorific-prefix',
            error: errors?.[`${prefix}promo`],
            control
        }
    }
    return fields
}
